/*
Author: Jude Fernando
Date: 25/09/2024
Copyright © 2024 Infix!. All rights reserved.
*/

import Utility from '@/utils/Utility'
import React, { FormEvent, useEffect, useMemo, useRef, useState } from 'react'

import { RootState } from '@/reducers/store'
import { useSelector } from 'react-redux'
import {
    CommentNode,
    DropDownNode,
    HealthImprovementPOA,
    HealthImprovmentPatient,
    SingleChoiceNode,
} from './CustomizedSurveyNodes'
import DateRangeMatrixNode from './CustomizedSurveyNodes/DateRangeMatrix/DateRangeMatrixNode'
import { INode, NodeProps } from './SurveyModel/types/nodeTypes'
import Debug from './SurveyNodes/Debug'
import FormRow from './SurveyNodes/FormRow'
import Group from './SurveyNodes/Group'
import HeightInput from './SurveyNodes/HeightInput'
import MultipleChoiceInput from './SurveyNodes/MultipleChoiceInput'
import NumberInput from './SurveyNodes/NumberInput'
import QuestionSet from './SurveyNodes/QuestionSet'
import Root from './SurveyNodes/Root'
import Section from './SurveyNodes/Section'
//import Text from "./SurveyNodes/Text";
import withAlerts from '@/components/HOC/withAlerts'
//import Actions from "./CustomizedSurveyNodes/Actions/Actions";
import { useValidate } from '@/state/survey/formSlice'
import { useResetGetQUestionAnswers } from '@/state/survey/poaSurveySlice'
import AdvancedMultiChoiceCheckbox from './CustomizedSurveyNodes/AdvancedMultiChoiceCheckbox/AdvancedMultiChoiceCheckbox'
import Complete from './CustomizedSurveyNodes/Complete/Complete'
import DatePicker from './CustomizedSurveyNodes/DatePicker/DatePicker'
import HtmlContent from './CustomizedSurveyNodes/HtmlContent/HtmlContent'
import InstructionsAndInformation from './CustomizedSurveyNodes/InstructionsAndInformation/InstructionsAndInformation'
import MDASI4QRiskScore from './CustomizedSurveyNodes/MDASI4QRiskScore/MDASI4QRiskScore'
import QuestionTextOnly from './CustomizedSurveyNodes/QuestionTextOnly/QuestionTextOnly'
import RepeatableMatrix from './CustomizedSurveyNodes/RepeatableMatrix/RepeatableMatrix'
import SingleLineText from './CustomizedSurveyNodes/SingleLineText/SingleLineText'
import TelephoneNo from './CustomizedSurveyNodes/TelephoneNo/TelephoneNo'
import Textarea from './CustomizedSurveyNodes/Textarea/Textarea'
import WeightInput from './SurveyNodes/WeightInput'
import HeightAndWeightInput from './CustomizedSurveyNodes/HeightAndWeightInput/HeightAndWeightInput'
import SleepPOA from './CustomizedSurveyNodes/Sleep/SleepPOA'
import InfoList from './CustomizedSurveyNodes/InfoList'
import Concern from './SurveyNodes/Concern'

const nodeMap: { [key: string]: React.FC } = {
    section: withAlerts(Section),
    group: withAlerts(Group),
    root: withAlerts(Root),
    SingleLineText: withAlerts(SingleLineText),
    TelephoneNo: withAlerts(TelephoneNo),
    number: withAlerts(NumberInput),
    WeightInputPanel: withAlerts(WeightInput),
    HeightInputPanel: withAlerts(HeightInput),
    SingleChoice: withAlerts(SingleChoiceNode), //SingleChoiceInput,
    multiChoice: withAlerts(MultipleChoiceInput),
    SingleSelect: withAlerts(DropDownNode),
    HealthImprovementPOA: withAlerts(HealthImprovementPOA),
    HealthImprovmentPatient: withAlerts(HealthImprovmentPatient),
    DateRangeMatrix: withAlerts(DateRangeMatrixNode),
    Comment: withAlerts(CommentNode),
    QuestionSet: withAlerts(QuestionSet),
    FormRow: withAlerts(FormRow),
    Root: withAlerts(Root),
    Section: withAlerts(Section),
    AdvancedMultiChoiceCheckbox: withAlerts(AdvancedMultiChoiceCheckbox),
    MultiChoiceCheckbox: withAlerts(AdvancedMultiChoiceCheckbox),
    QuestionTextOnly: withAlerts(QuestionTextOnly),
    RepeatableMatrix: withAlerts(RepeatableMatrix),
    Textarea: withAlerts(Textarea),
    MDASI4QRiskScore: withAlerts(MDASI4QRiskScore),
    HtmlContent: withAlerts(HtmlContent),
    DatePicker: withAlerts(DatePicker),
    Actions: withAlerts(RepeatableMatrix),
    InstructionsAndInformation: withAlerts(InstructionsAndInformation),
    Complete: withAlerts(Complete),
    HeightAndWeight: withAlerts(HeightAndWeightInput),
    Sleep: withAlerts(SleepPOA),
    InfoList: withAlerts(InfoList),
    Concern: Concern,
}

const QuestionNode: React.FC<NodeProps> = (props) => {
    const {
        node,
        name,
        disabled = false,
        setHeight,
        height,
        handleScroll,
    } = props
    
    const [active, setActive] = useState(true)
    const validateInputField = useValidate()
    const resetQuestionnaireAnswers = useResetGetQUestionAnswers()
    // const { isPatient } = useUrlType();
    const prevEvalRef = useRef<boolean>()
    const form = useSelector((state: RootState) => state.form)
    const Component =
        (nodeMap[node.type] as unknown as React.FC<NodeProps>) || Debug
    const { selectedTab } = useSelector((state: RootState) => state.poaSurvey)

    useEffect(() => {
        return () => {
            resetQuestionnaireAnswers()
        }
        
    }, [])

    const evalResult = useMemo(() => {
        return node.showIf ? !!Utility.evaluateExp(node, form) : true
    }, [node, form])

    useEffect(() => {
        setActive(evalResult)
        if (prevEvalRef.current !== evalResult) {
            //ToDo:  this solution will not work for Patient view. Need to excecute this logic when the value is saved.
            if (!evalResult && form[node.name]) {
                validateInputField(node.name, '', true)
            }
            prevEvalRef.current = evalResult
        }
    }, [evalResult])

    if (node.hidden) return null;
    return (
        <React.Fragment>
            {(node.name === 'InstructionsAndInformationSection' &&
                selectedTab === 'patient') ||
            ((node.name === 'Allergies') &&
                (node.type === 'section' || node.type === 'Section')) ? null : (
                <React.Fragment>
                    {active && (
                        <Component
                            handleScroll={handleScroll}
                            height={height}
                            setHeight={setHeight}
                            disabled={
                                Object.prototype.hasOwnProperty.call(
                                    node,
                                    'disabled'
                                ) && node.disabled
                                    ? node.disabled
                                    : disabled
                            }
                            key={name}
                            className={node.type}
                            node={node}
                            name={name}
                        >
                            {node.nodes && node.nodes.length > 0 && (
                                <React.Fragment>
                                    {node.nodes.map((n: INode, i: number) => (
                                        <>
                                            <div id={n.name} className="absolute"></div>
                                            <QuestionNode
                                                handleScroll={handleScroll}
                                                name={n.name || node.name}
                                                key={i}
                                                node={n}
                                                disabled={disabled}
                                                setHeight={setHeight}
                                            ></QuestionNode>
                                        </>
                                    ))}
                                </React.Fragment>
                            )}
                        </Component>
                    )}
                </React.Fragment>
            )}
        </React.Fragment>
    )
}

const SurveyForm: React.FC<{
    config: INode
    disabled: boolean
    height: number
    setHeight: (height: number) => void
    handleScroll: () => void
}> = (props) => {
    const handleSubmit = (e: FormEvent) => {
        e.preventDefault()
    }

    return (
        <form onSubmit={(e) => handleSubmit(e)}>
            <QuestionNode
                handleScroll={props.handleScroll}
                height={props.height}
                setHeight={props.setHeight}
                node={props.config}
                disabled={props.disabled}
            />
            {/* <pre>{JSON.stringify(formData)}</pre> */}
        </form>
    )
}

const Survey = (props: any) => {
    const { config, disabled, setHeight, height, handleScroll } = props
    return (
        <div>
            <SurveyForm
                handleScroll={handleScroll}
                height={height}
                setHeight={setHeight}
                disabled={disabled}
                config={config}
            />
        </div>
    )
}
export { QuestionNode }
export default Survey
